import { inject, Injectable } from '@angular/core';
import { ICoreSchoolBlackoutLocalDto } from '@nova/types';
import { interval, Observable, of } from 'rxjs';
import { catchError, exhaustMap, finalize, map, shareReplay, startWith, tap } from 'rxjs/operators';

import { IStudentSchoolTimerData } from 'app/student/services/time-checker/models/student-school-timer-data';
import { StudentTimeCheckerHttpService } from 'app/student/services/time-checker/student-time-checker-http.service';

import { environment } from 'env/environment';

@Injectable({
  providedIn: 'root'
})
export class StudentSchoolTimeCheckerService {
  private readonly httpService = inject(StudentTimeCheckerHttpService);

  private readonly CACHE_REFRESH_BUFFER_MS = 5000;
  private readonly DEV_TOOLS_CACHE_OVERRIDE_MS = 60000;
  private errorSyncState: ICoreSchoolBlackoutLocalDto = {
    isSchoolHours: true,
    userLocalTime: new Date(),
    startOfSchoolHoursLocal: new Date(),
    endOfSchoolHoursLocal: new Date(Date.now() + Number.MAX_SAFE_INTEGER),
    startOfSchoolHoursNextLocal: new Date(),
    endOfSchoolHoursNextLocal: new Date(),
    nextChangeOverLocal: new Date(Date.now() + Number.MAX_SAFE_INTEGER),
    isBetweenSchoolPeriods: false
  };

  private cachedState?: ICoreSchoolBlackoutLocalDto;
  private cacheTime = 0; // When we cached the server data
  private _inFlight$: Observable<ICoreSchoolBlackoutLocalDto> | null = null;

  private _devToolsCacheOverrideUntil = 0;

  public syncState(): Observable<ICoreSchoolBlackoutLocalDto> {
    // Check if we have valid cached data
    if (this.cachedState && !this.isCacheExpired()) {
      return of(this.getCurrentState());
    }

    // Share a single in-flight HTTP request across all concurrent callers
    if (this._inFlight$) {
      return this._inFlight$;
    }

    this._inFlight$ = this.httpService.getInfo().pipe(
      tap((state: ICoreSchoolBlackoutLocalDto) => {
        // Fresh data cached successfully
        this.cachedState = state;
        this.cacheTime = Date.now(); // Record when we cached this data

        // Test environment: Auto-apply cache override if fetched data has been shifted to a time beyond the next changeover
        if (!environment.production && this.timeTillChangeOver() <= this.CACHE_REFRESH_BUFFER_MS) {
          this.setDevToolsCacheOverride();
        }
      }),
      map(() => this.getCurrentState()),
      catchError((error: unknown) => {
        console.error('Sync failed, defaulting to SCHOOL ON for safety:', error);
        this.cachedState = this.errorSyncState; // Already has isSchoolHours: true
        return of(this.getCurrentState());
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => { this._inFlight$ = null; })
    );

    return this._inFlight$;
  }

  public isSchoolHours(): boolean {
    if (!this.cachedState) {
      return true; // Default to "school on" if no data available
    }
    // Trust the server's calculation
    return this.cachedState.isSchoolHours;
  }

  public isSchoolHoursObservable(): Observable<boolean> {
    return this.syncState().pipe(map(() => this.isSchoolHours()));
  }

  public timeTillChangeOver(): number {
    if (!this.cachedState || !this.cacheTime) {
      return 0;
    }

    // Calculate remaining time as of when server responded
    const userLocalTime = new Date(this.cachedState.userLocalTime).getTime();
    const changeOverTime = new Date(this.cachedState.nextChangeOverLocal).getTime();
    const originalRemaining = changeOverTime - userLocalTime;

    // Calculate how much time has passed since we cached the data
    const timeElapsed = Date.now() - this.cacheTime;

    // Subtract elapsed time from original remaining time
    const currentRemaining = originalRemaining - timeElapsed;

    return Math.max(0, currentRemaining);
  }

  public getRemainingSchoolTimeMs(): number {
    // Only show countdown when IN school hours
    if (!this.isSchoolHours()) {
      return 0; // This will hide the timer (!!timeMs = false)
    }
    return this.timeTillChangeOver();
  }

  // Cache management for dev tools
  public bustCache(): void {
    this.cachedState = undefined;
    this.cacheTime = 0;
    this._devToolsCacheOverrideUntil = 0;
  }

  /**
   * Prevents cache from being considered expired for the given duration.
   * Used by dev tools (time-shifter, school-hours-forcer) to avoid infinite
   * refresh loops
   */
  public setDevToolsCacheOverride(durationMs: number = this.DEV_TOOLS_CACHE_OVERRIDE_MS): void {
    this._devToolsCacheOverrideUntil = Date.now() + durationMs;
  }

  // For components that need countdown displays - provides live updates every second
  public getCooldownTimer(): Observable<IStudentSchoolTimerData> {
    return interval(1000).pipe(
      startWith(0),
      exhaustMap(() => this.syncState()),
      map(() => ({
        isActive: this.isSchoolHours(),
        remainingSchoolTimeMs: this.getRemainingSchoolTimeMs()
      }))
    );
  }

  // Helper methods for cache management
  private isCacheExpired(): boolean {
    if (!this.cachedState || !this.cacheTime) {
      return true;
    }

    // Dev tools override: skip time-based expiry to prevent infinite refresh
    if (!environment.production && Date.now() < this._devToolsCacheOverrideUntil) {
      return false;
    }

    // Use server-based delta (same logic as countdown) to avoid timezone/skew issues
    const timeUntilChange = this.timeTillChangeOver();
    const expired = timeUntilChange <= this.CACHE_REFRESH_BUFFER_MS;
    return expired;
  }

  private getCurrentState(): ICoreSchoolBlackoutLocalDto {
    if (!this.cachedState) {
      return this.errorSyncState;
    }

    // Return cached state as-is - trust the server's calculation
    return this.cachedState;
  }
}
